import React, { useState } from "react";
import i18n from "i18next";
import { useDispatch } from "react-redux";
import {
  updateScene,
  addScenarioElement,
  removeScenarioElement,
  updateScenarioElement,
  updateScenarioMeta,
} from "../../../common/actions";
import { defaultForElement, makeId, ELEMENT_TYPES } from "../schema";
import { ActionListEditor } from "./ActionEditor";

/* eslint-disable react/prop-types */

const TYPE_ICONS = {
  visualtext: "article",
  flipcard: "flip",
  truefalse: "rule",
  mcq: "quiz",
};

const TYPE_LABELS = {
  visualtext: i18n.t("InteractiveScenario.Type.VisualText") || "Metin Kartı",
  flipcard: i18n.t("InteractiveScenario.Type.FlipCard") || "Çift Yüzlü Kart",
  truefalse: i18n.t("InteractiveScenario.Type.TrueFalse") || "Doğru / Yanlış",
  mcq: i18n.t("InteractiveScenario.Type.MCQ") || "Çoktan Seçmeli",
};

function Field({ label, children }) {
  return (
    <div className="sce-field">
      <label className="sce-field__label">{label}</label>
      <div className="sce-field__control">{children}</div>
    </div>
  );
}

function ElementRow({ element, scenes, variables, boxId, sceneId, onDelete }) {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);

  const setField = (path, value) =>
    dispatch(updateScenarioElement(boxId, sceneId, element.id, path, value));

  const renderActionFields = () => {
    switch (element.type) {
      case "visualtext":
        return (
          <div className="sce-element-row__section">
            <label className="sce-field__label">
              {i18n.t("InteractiveScenario.Label.Content") || "İçerik"}
            </label>
            <textarea
              className="sce-action-editor__input sce-action-editor__textarea"
              value={element.content || ""}
              rows={3}
              onChange={(e) => setField(["content"], e.target.value)}
            />
            {(element.buttons || []).map((btn, bi) => (
              <div key={btn.id} className="sce-element-row__btn-block">
                <div className="sce-action-editor__row">
                  <input
                    className="sce-action-editor__input"
                    type="text"
                    placeholder={
                      i18n.t("InteractiveScenario.Placeholder.ButtonLabel") || "Buton etiketi"
                    }
                    value={btn.label || ""}
                    onChange={(e) => setField(["buttons", bi, "label"], e.target.value)}
                  />
                  <select
                    className="sce-action-editor__select"
                    value={btn.style || "primary"}
                    onChange={(e) => setField(["buttons", bi, "style"], e.target.value)}
                  >
                    {["primary", "secondary", "success", "danger"].map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="sce-action-editor__delete"
                    onClick={() => {
                      const next = (element.buttons || []).filter((_, i) => i !== bi);
                      setField(["buttons"], next);
                    }}
                  >
                    ✕
                  </button>
                </div>
                <ActionListEditor
                  actions={btn.actions || []}
                  scenes={scenes}
                  variables={variables}
                  onChange={(acts) => setField(["buttons", bi, "actions"], acts)}
                />
              </div>
            ))}
            <button
              type="button"
              className="sce-add-btn"
              onClick={() => {
                const next = [
                  ...(element.buttons || []),
                  {
                    id: makeId(),
                    label: i18n.t("InteractiveScenario.Default.Button") || "Buton",
                    style: "primary",
                    actions: [],
                  },
                ];
                setField(["buttons"], next);
              }}
            >
              {i18n.t("InteractiveScenario.Action.AddButton") || "+ Buton Ekle"}
            </button>
          </div>
        );

      case "flipcard":
        return (
          <div className="sce-element-row__section">
            <label className="sce-field__label">
              {i18n.t("InteractiveScenario.Label.FrontFace") || "Ön Yüz"}
            </label>
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={i18n.t("InteractiveScenario.Placeholder.Title") || "Başlık"}
              value={element.frontTitle || ""}
              onChange={(e) => setField(["frontTitle"], e.target.value)}
            />
            <textarea
              className="sce-action-editor__input sce-action-editor__textarea"
              placeholder={i18n.t("InteractiveScenario.Placeholder.Text") || "Metin"}
              rows={2}
              value={element.frontText || ""}
              onChange={(e) => setField(["frontText"], e.target.value)}
            />
            <label className="sce-field__label" style={{ marginTop: 8 }}>
              {i18n.t("InteractiveScenario.Label.BackFace") || "Arka Yüz"}
            </label>
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={i18n.t("InteractiveScenario.Placeholder.Title") || "Başlık"}
              value={element.backTitle || ""}
              onChange={(e) => setField(["backTitle"], e.target.value)}
            />
            <textarea
              className="sce-action-editor__input sce-action-editor__textarea"
              placeholder={i18n.t("InteractiveScenario.Placeholder.Text") || "Metin"}
              rows={2}
              value={element.backText || ""}
              onChange={(e) => setField(["backText"], e.target.value)}
            />
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={i18n.t("InteractiveScenario.Placeholder.ButtonLabel") || "Buton etiketi"}
              value={element.backButtonLabel || ""}
              onChange={(e) => setField(["backButtonLabel"], e.target.value)}
            />
            <label className="sce-field__label" style={{ marginTop: 8 }}>
              {i18n.t("InteractiveScenario.Label.BackActions") || "Arka Aksiyonlar"}
            </label>
            <ActionListEditor
              actions={element.backActions || []}
              scenes={scenes}
              variables={variables}
              onChange={(acts) => setField(["backActions"], acts)}
            />
          </div>
        );

      case "truefalse":
        return (
          <div className="sce-element-row__section">
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={i18n.t("InteractiveScenario.Placeholder.Question") || "Soru"}
              value={element.question || ""}
              onChange={(e) => setField(["question"], e.target.value)}
            />
            <div className="sce-action-editor__row">
              <input
                className="sce-action-editor__input"
                type="text"
                placeholder={
                  i18n.t("InteractiveScenario.Placeholder.TrueLabelDetailed") || "Doğru etiketi"
                }
                value={element.trueLabel || ""}
                onChange={(e) => setField(["trueLabel"], e.target.value)}
              />
              <input
                className="sce-action-editor__input"
                type="text"
                placeholder={
                  i18n.t("InteractiveScenario.Placeholder.FalseLabelDetailed") || "Yanlış etiketi"
                }
                value={element.falseLabel || ""}
                onChange={(e) => setField(["falseLabel"], e.target.value)}
              />
            </div>
            <div className="sce-action-editor__row">
              <label className="sce-field__label">
                {i18n.t("InteractiveScenario.Label.CorrectAnswer") || "Doğru cevap:"}
              </label>
              <select
                className="sce-action-editor__select"
                value={element.correctAnswer === true ? "true" : "false"}
                onChange={(e) => setField(["correctAnswer"], e.target.value === "true")}
              >
                <option value="true">{i18n.t("InteractiveScenario.Option.True") || "Doğru"}</option>
                <option value="false">
                  {i18n.t("InteractiveScenario.Option.False") || "Yanlış"}
                </option>
              </select>
            </div>
            <label className="sce-field__label" style={{ marginTop: 8 }}>
              {i18n.t("InteractiveScenario.Label.CorrectActions") || "Doğru Aksiyonlar"}
            </label>
            <ActionListEditor
              actions={element.correctActions || []}
              scenes={scenes}
              variables={variables}
              onChange={(acts) => setField(["correctActions"], acts)}
            />
            <label className="sce-field__label" style={{ marginTop: 8 }}>
              {i18n.t("InteractiveScenario.Label.IncorrectActions") || "Yanlış Aksiyonlar"}
            </label>
            <ActionListEditor
              actions={element.incorrectActions || []}
              scenes={scenes}
              variables={variables}
              onChange={(acts) => setField(["incorrectActions"], acts)}
            />
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={
                i18n.t("InteractiveScenario.Placeholder.ExplanationOptional") ||
                "Açıklama (isteğe bağlı)"
              }
              style={{ marginTop: 8 }}
              value={element.explanation || ""}
              onChange={(e) => setField(["explanation"], e.target.value)}
            />
          </div>
        );

      case "mcq":
        return (
          <div className="sce-element-row__section">
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={i18n.t("InteractiveScenario.Placeholder.Question") || "Soru"}
              value={element.question || ""}
              onChange={(e) => setField(["question"], e.target.value)}
            />
            {(element.choices || []).map((choice, ci) => (
              <div key={choice.id} className="sce-element-row__choice-block">
                <div className="sce-action-editor__row">
                  <span className="sce-element-row__choice-label">{ci + 1}.</span>
                  <input
                    className="sce-action-editor__input"
                    type="text"
                    placeholder={
                      i18n.t("InteractiveScenario.Placeholder.ChoiceText") || "Seçenek metni"
                    }
                    value={choice.text || ""}
                    onChange={(e) => setField(["choices", ci, "text"], e.target.value)}
                  />
                  <button
                    type="button"
                    className="sce-action-editor__delete"
                    onClick={() => {
                      const next = (element.choices || []).filter((_, i) => i !== ci);
                      setField(["choices"], next);
                    }}
                  >
                    ✕
                  </button>
                </div>
                <ActionListEditor
                  actions={choice.actions || []}
                  scenes={scenes}
                  variables={variables}
                  onChange={(acts) => setField(["choices", ci, "actions"], acts)}
                />
              </div>
            ))}
            <button
              type="button"
              className="sce-add-btn"
              onClick={() => {
                const next = [...(element.choices || []), { id: makeId(), text: "", actions: [] }];
                setField(["choices"], next);
              }}
            >
              {i18n.t("InteractiveScenario.Action.AddChoice") || "+ Seçenek Ekle"}
            </button>
            <input
              className="sce-action-editor__input"
              type="text"
              placeholder={
                i18n.t("InteractiveScenario.Placeholder.ExplanationOptional") ||
                "Açıklama (isteğe bağlı)"
              }
              style={{ marginTop: 8 }}
              value={element.explanation || ""}
              onChange={(e) => setField(["explanation"], e.target.value)}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`sce-element-row${expanded ? " sce-element-row--expanded" : ""}`}>
      <div className="sce-element-row__header" onClick={() => setExpanded((e) => !e)}>
        <span
          className="material-icons sce-element-row__type-icon"
          title={TYPE_LABELS[element.type]}
        >
          {TYPE_ICONS[element.type] || "widgets"}
        </span>
        <span className="sce-element-row__title">{element.title || TYPE_LABELS[element.type]}</span>
        <span className="sce-element-row__chevron material-icons">
          {expanded ? "expand_less" : "expand_more"}
        </span>
        <button
          type="button"
          className="sce-action-editor__delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title={i18n.t("Delete") || "Elementi sil"}
        >
          ✕
        </button>
      </div>

      {expanded && (
        <div className="sce-element-row__body">
          <Field label={i18n.t("InteractiveScenario.Label.Title") || "Başlık"}>
            <input
              className="sce-action-editor__input"
              type="text"
              value={element.title || ""}
              onChange={(e) => setField(["title"], e.target.value)}
            />
          </Field>
          {renderActionFields()}
        </div>
      )}
    </div>
  );
}

export default function SceneDetailPanel({ scene, scenario, boxId }) {
  const dispatch = useDispatch();
  const [addingType, setAddingType] = useState(null);

  const scenes = scenario.scenes || [];
  const variables = scenario.variables || [];

  const setSceneProp = (patch) => dispatch(updateScene(boxId, scene.id, patch));

  const handleAddElement = (type) => {
    const el = defaultForElement(type);
    dispatch(addScenarioElement(boxId, scene.id, el));
    setAddingType(null);
  };

  const handleSetStart = () => dispatch(updateScenarioMeta(boxId, { startSceneId: scene.id }));

  return (
    <div className="sce-detail-panel">
      <div className="sce-detail-panel__header">
        <h3 className="sce-detail-panel__title">
          {scene.title || i18n.t("scene") || "Sahne"}
          {scenario.startSceneId === scene.id && (
            <span className="sce-detail-panel__start-badge">
              {i18n.t("InteractiveScenario.Label.Start") || "BAŞLANGIÇ"}
            </span>
          )}
        </h3>
        {scenario.startSceneId !== scene.id && (
          <button type="button" className="sce-add-btn" onClick={handleSetStart}>
            {i18n.t("InteractiveScenario.Action.MakeStart") || "Başlangıç Yap"}
          </button>
        )}
      </div>

      <div className="sce-detail-panel__section">
        <Field label={i18n.t("InteractiveScenario.Label.SceneTitle") || "Sahne Başlığı"}>
          <input
            className="sce-action-editor__input"
            type="text"
            value={scene.title || ""}
            onChange={(e) => setSceneProp({ title: e.target.value })}
          />
        </Field>
        <div className="sce-detail-panel__colors">
          <Field label={i18n.t("InteractiveScenario.Label.Background") || "Arka Plan"}>
            <input
              type="color"
              className="sce-action-editor__color"
              value={scene.bgColor || "#1a1a2e"}
              onChange={(e) => setSceneProp({ bgColor: e.target.value })}
            />
          </Field>
          <Field label={i18n.t("InteractiveScenario.Label.TextColor") || "Yazı Rengi"}>
            <input
              type="color"
              className="sce-action-editor__color"
              value={scene.textColor || "#ffffff"}
              onChange={(e) => setSceneProp({ textColor: e.target.value })}
            />
          </Field>
          <Field label={i18n.t("InteractiveScenario.Label.Pattern") || "Desen"}>
            <select
              className="sce-action-editor__select"
              value={scene.bgPattern || "none"}
              onChange={(e) => setSceneProp({ bgPattern: e.target.value })}
            >
              {["none", "dots", "grid", "waves"].map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </div>

      <div className="sce-detail-panel__section">
        <div className="sce-detail-panel__section-header">
          <span>{i18n.t("Elements") || "Elementler"}</span>
          <span className="sce-detail-panel__element-count">{(scene.elements || []).length}</span>
        </div>

        {(scene.elements || []).map((el) => (
          <ElementRow
            key={el.id}
            element={el}
            scenes={scenes}
            variables={variables}
            boxId={boxId}
            sceneId={scene.id}
            onDelete={() => dispatch(removeScenarioElement(boxId, scene.id, el.id))}
          />
        ))}

        {!addingType ? (
          <button
            type="button"
            className="sce-add-btn sce-add-btn--primary"
            onClick={() => setAddingType("pick")}
          >
            {i18n.t("InteractiveScenario.Action.AddElement") || "+ Element Ekle"}
          </button>
        ) : (
          <div className="sce-type-picker">
            {ELEMENT_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                className="sce-type-picker__btn"
                onClick={() => handleAddElement(t)}
              >
                <span className="material-icons">{TYPE_ICONS[t]}</span>
                {TYPE_LABELS[t]}
              </button>
            ))}
            <button
              type="button"
              className="sce-type-picker__cancel"
              onClick={() => setAddingType(null)}
            >
              {i18n.t("Cancel") || "İptal"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
